import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import ClinicTemplateInput from './ClinicTemplateInput';
import { customerDisplayName } from '@src/helpers/CustomerDisplay';
import { Button, ButtonBase, InputAdornment, TextField, MenuItem } from '@mui/material';
import api from '@src/api/axiosInstance';
import { useAppSelector } from '@src/store/hooks';
import '../../styles/SendTemplateMessage.css';
import FileInput from '../FileInput';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import {
	componentHasMediaFormat,
	generateFinalTemplateParams,
	generateTemplateParamsByValues,
	getTemplateParams,
	extractParameterKey,
} from '@src/helpers/TemplateMessageHelper';
import {
	isImageSupported,
	isVideoSupported,
} from '@src/helpers/MediaFilesHelper';
import { useTranslation } from 'react-i18next';
import PubSub from 'pubsub-js';
import {
	EVENT_TOPIC_SEND_TEMPLATE_MESSAGE_ERROR,
	FORM_VALIDATION_ERROR,
	InvalidTemplateParamException,
} from '@src/Constants';
import PublishIcon from '@mui/icons-material/Publish';
import LinkIcon from '@mui/icons-material/Link';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { AxiosError } from 'axios';
import { Template } from '@src/types/templates';
import { createMedia } from '@src/api/mediaApi';
import { isStringNumber } from '@src/helpers/IntegerHelper';

interface Props {
	data: Template;
	setSending: (value: boolean) => void;
	setErrors: (value: string[]) => void;
	send: (data: Template) => void;
	sendButtonInnerRef: React.Ref<HTMLButtonElement>;
}

const SendTemplateMessage: React.FC<Props> = ({
	data,
	setSending,
	setErrors,
	send,
	sendButtonInnerRef,
}) => {
	const { t } = useTranslation();

	const template = data;
	const clinicComposer = window.config?.APP_ENV_NAME === 'cvparc-shadow';
	const waId = useAppSelector(state => state.waId.value);
	const [context, setContext] = useState<any>(null);
	const [clientId, setClientId] = useState('');
	const [contextError, setContextError] = useState('');
	const client = context?.clients.find((c: any) => c.id === clientId);
	const labels: Record<string, string> = {customer:'Cliente',sender:'Profesional',pet:'Mascota',vet:'Veterinario',date:'Fecha',time:'Hora',document:'Documento pendiente',order:'Producto o pedido'};
	const roleFor = (type: string, position: number) => type === 'BODY' ? template.fields?.[position] : undefined;

	const [params, setParams] = useState<{
		[key: string | number]: { [key: string | number]: any };
	}>({});
	const [headerFileURL, setHeaderFileURL] = useState('');
	const [isUploading, setUploading] = useState(false);
	const [provideFileBy, setProvideFileBy] = useState<
		'upload' | 'file_url' | undefined
	>();

	const FILE_PROVIDE_TYPE_UPLOAD = 'upload';
	const FILE_PROVIDE_TYPE_FILE_URL = 'file_url';

	const headerFileInput = useRef<HTMLInputElement>();

	useEffect(() => {
		setParams(generateTemplateParamsByValues(template, undefined));
		setContext(null); setClientId(''); setContextError('');
		if (!template.fields) return;
		let cancelled = false;
		api.get('/template-context/', {params:{wa_id:waId}}).then(({data}) => {
			if (cancelled) return;
			setContext(data);
			setClientId(data.clients.length === 1 ? data.clients[0].id : '');
		}).catch(() => {if (!cancelled) setContextError('No se han podido cargar los datos de Wakyma.');});
		return () => {cancelled = true;};
	}, [template.name, waId]);

	useEffect(() => {
		if (!context) return;
		const fullName = client?.name ?? '';
		const token = (fullName.includes(',') ? fullName.split(',').slice(1).join(' ') : fullName).trim().split(/\s+/)[0];
		const firstName = token ? token.charAt(0).toLocaleUpperCase('es') + token.slice(1).toLocaleLowerCase('es') : '';
		setParams(previous => {
			const next = JSON.parse(JSON.stringify(previous));
			template.components?.forEach((component, index) => getTemplateParams(component.text).forEach((parameter, position) => {
				const key = extractParameterKey(parameter);
				const role = roleFor(component.type, position);
				if (key && next[index]?.[key] && ['sender','customer','pet'].includes(role ?? '')) {
					next[index][key].text = role === 'sender' ? context.sender : role === 'customer' ? firstName : client?.pets.length === 1 ? client.pets[0].name : '';
				}
			}));
			return next;
		});
	}, [context, clientId]);

	useEffect(() => {
		// Update params when header image changes
		setParams((prevState) => {
			if (prevState[0]?.[0]?.['image']) {
				prevState[0][0]['image']['link'] = headerFileURL;
			} else if (prevState[0]?.[0]?.['video']) {
				prevState[0][0]['video']['link'] = headerFileURL;
			} else if (prevState[0]?.[0]?.['document']) {
				prevState[0][0]['document']['link'] = headerFileURL;
			}

			return prevState;
		});
		setErrors([]);
	}, [headerFileURL, params]);

	const updateParam = (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		index: number,
		paramKey: string | null
	) => {
		setParams((prevState) => {
			const nextState = prevState;

			if (!!paramKey && nextState[index][paramKey]) {
				nextState[index][paramKey].text = event.target.value;
				// Repeated pet placeholders share one staff selection.
				if (roleFor(template.components?.[index]?.type ?? '', Number(paramKey)-1) === 'pet') {
					template.fields?.forEach((role, position) => {
						if (role === 'pet' && nextState[index][String(position+1)]) nextState[index][String(position+1)].text = event.target.value;
					});
				}

				// Inject parameter name if key is not a number
				if (!isStringNumber(paramKey)) {
					nextState[index][paramKey].parameter_name = paramKey;
				}
			}

			return { ...nextState };
		});
	};

	const sendAfterCheck = () => {
		if (template.fields && (!context || !clientId || (template.fields.includes('sender') && !context.sender))) {
			setErrors(['Selecciona un cliente de Wakyma y espera a que se carguen los datos.']);
			setSending(false); return;
		}
		let hasError = false;
		const preparedParams = generateFinalTemplateParams(
			template,
			params,
			true,
			(error) => {
				if (error.type === FORM_VALIDATION_ERROR) {
					PubSub.publish(EVENT_TOPIC_SEND_TEMPLATE_MESSAGE_ERROR, [
						{
							title: t('Missing parameters'),
							details: error.message,
						},
					]);
					hasError = true;
				} else if (error === InvalidTemplateParamException) {
					PubSub.publish(EVENT_TOPIC_SEND_TEMPLATE_MESSAGE_ERROR, [
						{
							title: t('Invalid parameters'),
							details: t(
								'Parameters cannot have new-line/tab characters or more than 3 consecutive spaces!'
							),
						},
					]);
					hasError = true;
				} else {
					throw error;
				}

				setSending(false);
			}
		);

		if (hasError) return;

		const finalData = { ...template };
		finalData.params = Object.values(preparedParams);

		send(finalData);

		/*Object.entries(params).forEach((paramEntry) => {
			finalData.components[paramEntry[0]].params = paramEntry[1];
		});*/
	};

	const handleChosenMedia = async (
		file: FileList | undefined,
		format: string
	) => {
		if (!file) return;

		// Image
		if (file[0].type.startsWith('image/')) {
			if (format !== 'IMAGE' || !isImageSupported(file[0].type)) {
				PubSub.publish(EVENT_TOPIC_SEND_TEMPLATE_MESSAGE_ERROR, [
					{
						title: t('Unsupported file type'),
						details: t('Please choose a supported image type (png, jpg)'),
					},
				]);

				return;
			} else {
				setErrors([]);
			}
		}

		// Video
		if (file[0].type.startsWith('video/')) {
			if (format !== 'VIDEO' || !isVideoSupported(file[0].type)) {
				PubSub.publish(EVENT_TOPIC_SEND_TEMPLATE_MESSAGE_ERROR, [
					{
						title: t('Unsupported file type'),
						details: t('Please choose a supported video type (mp4, 3gp)'),
					},
				]);

				return;
			} else {
				setErrors([]);
			}
		}

		const formData = new FormData();
		formData.append('file_encoded', file[0]);

		setUploading(true);

		try {
			const data = await createMedia(formData);
			const fileURL = data.file;
			setHeaderFileURL(fileURL);
		} catch (error: any | AxiosError) {
			console.error(error);
			if (error?.response?.status === 413) {
				window.displayCustomError('The media file is too big to upload!');
			} else {
				window.displayError(error);
			}
		} finally {
			setUploading(false);
		}
	};

	const getMimetypeByFormat = (format: string) => {
		if (format === 'IMAGE') return 'image/jpeg, image/png';
		if (format === 'VIDEO') return 'video/mp4, video/3gpp';
		if (format === 'DOCUMENT') return '*/*';
	};

	// Full sentences per format, so translations can inflect them freely
	const getUploadLabel = (format: string, hasFile: boolean) => {
		if (format === 'IMAGE')
			return hasFile ? t('Upload another image') : t('Upload image');
		if (format === 'VIDEO')
			return hasFile ? t('Upload another video') : t('Upload video');
		if (format === 'DOCUMENT')
			return hasFile ? t('Upload another document') : t('Upload document');
		return hasFile ? t('Upload another file') : t('Upload a file');
	};

	return (
		<div className="sendTemplateMessage">
			{!clinicComposer && <h4 className="sendTemplateMessage__title">{template.display_name ?? template.name}</h4>}
			{template.staff_description && <p>{template.staff_description}</p>}
			{contextError && <Alert severity="error">{contextError}</Alert>}
			{context?.clients.length > 1 && <TextField className={clinicComposer ? 'clinic-template-client' : undefined} size="small" select fullWidth label="Cliente de Wakyma" value={clientId} onChange={event => setClientId(event.target.value)}>
				{context.clients.map((c: any) => <MenuItem key={c.id} value={c.id}>{customerDisplayName(c.name)}</MenuItem>)}
			</TextField>}

			{template.components?.map((comp, compIndex) => (
				<div key={compIndex} className="sendTemplateMessage__component">
					<div className="sendTemplateMessage__section">
						{!clinicComposer && <h6>{comp.type}</h6>}
						<div>
							{componentHasMediaFormat(comp) && (
								<div>
									<div className="sendTemplateMessage__section__fileType">
										{t('Type: %s', comp.format?.toLowerCase())}
									</div>

									{headerFileURL && (
										<div className="sendTemplateMessage__section__headerImage__preview">
											{comp.format === 'IMAGE' && (
												<img src={headerFileURL} alt="header" />
											)}
											{comp.format === 'VIDEO' && (
												<video src={headerFileURL} controls />
											)}
											{comp.format === 'DOCUMENT' && (
												<div className="sendTemplateMessage__section__headerImage__preview__document">
													<AttachFileIcon />
													<span>{t('Document')}</span>
												</div>
											)}
										</div>
									)}

									{!provideFileBy && (
										<div className="sendTemplateMessage__section__provideFileChoices">
											<ButtonBase
												component="div"
												onClick={() =>
													setProvideFileBy(FILE_PROVIDE_TYPE_UPLOAD)
												}
												className="sendTemplateMessage__section__provideFileChoices__choice"
											>
												<PublishIcon />
												<span>{t('Upload a file')}</span>
											</ButtonBase>
											<ButtonBase
												component="div"
												onClick={() =>
													setProvideFileBy(FILE_PROVIDE_TYPE_FILE_URL)
												}
												className="sendTemplateMessage__section__provideFileChoices__choice"
											>
												<LinkIcon />
												<span>{t('Enter a link to file')}</span>
											</ButtonBase>
										</div>
									)}

									{provideFileBy === FILE_PROVIDE_TYPE_UPLOAD && (
										<div>
											{headerFileURL && (
												<div>
													<Alert severity="success">
														<AlertTitle>
															{t('Uploaded successfully')}
														</AlertTitle>
														<a href={headerFileURL} target="_blank">
															{headerFileURL}
														</a>
													</Alert>
												</div>
											)}

											<FileInput
												innerRef={headerFileInput}
												multiple={false}
												accept={getMimetypeByFormat(comp.format ?? '')}
												handleSelectedFiles={(file) =>
													handleChosenMedia(file, comp.format ?? '')
												}
											/>

											<div className="sendTemplateMessage__section__uploadWrapper">
												<Button
													variant="contained"
													color="primary"
													onClick={() => headerFileInput.current?.click()}
													disabled={isUploading}
													startIcon={<PublishIcon />}
												>
													{getUploadLabel(
														comp.format ?? '',
														Boolean(headerFileURL)
													)}
												</Button>

												{headerFileURL && (
													<Button
														variant="contained"
														color="secondary"
														onClick={() => setHeaderFileURL('')}
													>
														{t('Delete')}
													</Button>
												)}
											</div>

											<div className="sendTemplateMessage__section__provideFileOtherChoice">
												<Button
													color="secondary"
													onClick={() =>
														setProvideFileBy(FILE_PROVIDE_TYPE_FILE_URL)
													}
												>
													{t('Enter a link to file instead')}
												</Button>
											</div>
										</div>
									)}

									{provideFileBy === FILE_PROVIDE_TYPE_FILE_URL && (
										<div>
											<TextField
												variant="standard"
												value={headerFileURL}
												onChange={(event) =>
													setHeaderFileURL(event.target.value)
												}
												label={t('Link to file')}
												type="text"
												autoFocus
												fullWidth
											/>

											<div className="sendTemplateMessage__section__provideFileOtherChoice">
												<Button
													color="secondary"
													onClick={() =>
														setProvideFileBy(FILE_PROVIDE_TYPE_UPLOAD)
													}
												>
													{t('Upload a file instead')}
												</Button>
											</div>
										</div>
									)}
								</div>
							)}

							{!clinicComposer && comp.text}
							<div className={clinicComposer ? 'clinic-template-fields' : undefined}>
								{getTemplateParams(comp.text).map((param, paramIndex) => (
									clinicComposer && (roleFor(comp.type,paramIndex) === 'sender' || (roleFor(comp.type,paramIndex) === 'pet' && template.fields?.indexOf('pet') !== paramIndex)) ? null :
									clinicComposer && ['pet','date','time'].includes(roleFor(comp.type,paramIndex) ?? '') ?
									<ClinicTemplateInput key={`${template.name}:${waId}:${compIndex}:${paramIndex}`}
									 role={roleFor(comp.type,paramIndex)!} value={params[compIndex]?.[extractParameterKey(param) ?? '']?.text ?? ''}
									 disabled={!context || !client} pets={client?.pets ?? []}
									 onChange={value => updateParam({target:{value}} as ChangeEvent<HTMLInputElement>,compIndex,extractParameterKey(param))} /> :
									<TextField
									variant={clinicComposer ? 'outlined' : 'standard'}
									 size="small"
										select={['pet','vet'].includes(roleFor(comp.type, paramIndex) ?? '')}
										InputProps={{readOnly:roleFor(comp.type, paramIndex) === 'sender'}}
										disabled={!!template.fields && (!context || (roleFor(comp.type,paramIndex) === 'pet' && !client))}
										value={
											params[compIndex]
												? params[compIndex][extractParameterKey(param) ?? '']
														?.text
												: ''
										}
										onChange={(event) =>
											updateParam(event, compIndex, extractParameterKey(param))
										}
										className="templateMessage__param"
										key={paramIndex}
										label={labels[roleFor(comp.type,paramIndex) ?? ''] ?? extractParameterKey(param)}
										fullWidth={true}
									>
										{['pet','vet'].includes(roleFor(comp.type,paramIndex) ?? '') && (roleFor(comp.type,paramIndex) === 'pet' ? client?.pets ?? [] : context?.vets ?? []).map((item: any) => <MenuItem key={item.id} value={item.name}>{item.name}</MenuItem>)}
									</TextField>
								))}
							</div>
							{clinicComposer && comp.text && <div className="clinic-template-preview" aria-label="Vista previa del mensaje">{comp.text.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, key) => params[compIndex]?.[key]?.text || '[' + (labels[template.fields?.[Number(key)-1] ?? ''] ?? 'pendiente') + ']').split(/(\*[^*\n]+\*)/g).map((part, index) => part.startsWith('*') && part.endsWith('*') ? <strong key={index}>{part.slice(1,-1)}</strong> : part)}</div>}
						</div>

						{comp.type === 'BUTTONS' && (
							<div className="templateMessage__buttons">
								{comp.buttons?.map((button: any, idx: number) => (
									<div className="templateMessage__buttons--button" key={idx}>
										<Button color="primary" variant="outlined" disabled>
											{button.text}
										</Button>

										{getTemplateParams(button.url).map((param, paramIndex) => (
											<TextField
												key={paramIndex}
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															{button.url.replace(/\{\{[\d]+\}\}/g, '')}
														</InputAdornment>
													),
												}}
												variant="standard"
												onChange={(event) =>
													updateParam(
														event,
														compIndex,
														extractParameterKey(param)
													)
												}
												value={
													params[compIndex]
														? params[compIndex][
																extractParameterKey(param) ?? ''
														  ]?.text
														: ''
												}
											/>
										))}
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			))}
			<Button
				ref={sendButtonInnerRef}
				onClick={sendAfterCheck}
				className="hidden"
			>
				{t('Send')}
			</Button>
		</div>
	);
};

export default SendTemplateMessage;
